import type { LoaderFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { motion } from 'framer-motion';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.5 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export const loader: LoaderFunction = async () => {
  return json({});
};

export default function Introduction() {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen bg-gradient-to-b from-background to-background/80"
    >
      {/* 히어로 섹션 */}
      <section className="pt-20 pb-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#fdfaf7]/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#b98e6a] to-[#b98e6a] bg-clip-text text-transparent">
              법무법인 휘상
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              <span className="text-[#b98e6a] font-semibold">휘상</span> [輝 - 빛날 휘, 相 –서로 상]
              <br />
              서로 함께 빛난다는 의미를 담고 있습니다
            </p>
          </motion.div>

          {/* 핵심 가치 섹션 */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-gray-800 text-center">핵심 가치</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {[
                {
                  title: '정확성',
                  description: '모든 문제를 명확히 분석하고, 가장 정확한 해결책을 제시합니다.',
                },
                {
                  title: '신뢰성',
                  description: '고객과의 약속을 지키고, 결과로 신뢰를 증명합니다.',
                },
                {
                  title: '공감성',
                  description: '의뢰인의 상황을 깊이 이해하고, 함께하는 마음으로 해결합니다.',
                },
                {
                  title: '전문성',
                  description: '분야별 전문가들이 특화된 최고의 서비스를 제공합니다.',
                },
              ].map((value, index) => (
                <motion.div
                  key={value.title}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#b98e6a] to-[#8b6b4f] rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-white p-6 rounded-lg">
                    <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-[#b98e6a] transition-colors duration-200">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 비전 섹션 */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-gray-800 text-center">비전</h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#b98e6a]/20 to-[#8b6b4f]/20 rounded-2xl blur-xl"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-100">
                  <div className="space-y-6">
                    <p className="text-xl font-bold text-[#8b6b4f] mb-4">
                      휘상(輝相)은 함께 빛나는 가치를 지향합니다.
                    </p>
                    <div className="space-y-4 text-gray-600 text-lg">
                      <p>
                        휘상은 '서로 빛나다'는 뜻처럼, 
                        의뢰인 한 사람, 한 기업의 가능성이 더욱 밝게 빛날 수 있도록 함께합니다.
                      </p>
                      <p>
                        우리는 형사, 부동산, 금융, 기업 분야에 대한 깊이 있는 전문성과 진심 어린 소통을 바탕으로, 
                        복잡한 문제를 선명하게 풀어내고, 당신의 여정에 확신과 가능성을 더하는 동반자가 되겠습니다.
                      </p>
                      <p>
                        휘상은 결과로 증명합니다.
                      </p>
                      <p>
                        오늘보다 더 나은 내일,
                      </p>
                      <p>
                        의뢰인과 함께 빛나는 길을 만들어갑니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 연혁 섹션 */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-gray-800 text-center">연혁</h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#b98e6a]/20 to-[#8b6b4f]/20 rounded-2xl blur-xl"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-100">
                  <div className="space-y-8">
                    <div className="flex items-start gap-6">
                      <div className="w-24 text-[#b98e6a] font-semibold text-xl">2025-01</div>
                      <div className="flex-1">
                        <div className="relative">
                          <div className="absolute -left-8 top-0 w-1 h-full bg-gradient-to-b from-[#b98e6a] to-[#8b6b4f] rounded-full"></div>
                          <div className="pl-4">
                            <p className="text-xl font-bold text-gray-800 mb-2">
                              법무법인 휘상 설립
                            </p>
                            <p className="text-gray-600">
                              서울 강남구 영동대로 611, 13층 (찬앤찬타워)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-6">
                      <div className="w-24 text-[#b98e6a] font-semibold text-xl">2025-02</div>
                      <div className="flex-1">
                        <div className="relative">
                          <div className="absolute -left-8 top-0 w-1 h-full bg-gradient-to-b from-[#b98e6a] to-[#8b6b4f] rounded-full"></div>
                          <div className="pl-4">
                            <p className="text-xl font-bold text-gray-800 mb-2">법률 서비스 확장</p>
                            <p className="text-gray-600">다양한 법률 분야 전문가 영입</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 주소 정보 */}
          {/* <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-12 text-gray-800 text-center">오시는 길</h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#b98e6a]/20 to-[#8b6b4f]/20 rounded-2xl blur-xl"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-100">
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-xl text-gray-800 font-medium mb-2">법무법인 휘상</p>
                      <p className="text-lg text-gray-600">
                        서울 강남구 영동대로 611, 13층 (찬앤찬타워)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div> */}
        </div>
      </section>
    </motion.div>
  );
}
